/**
 * Word Service - Encapsulates all Word.run() operations
 * Follows Microsoft's recommended pattern for Office add-ins
 */

import type {
  CommentReply,
  CurrentDocumentCommentEntry,
  CurrentDocumentContextEntry,
  ReviewComment,
} from '../../types'
import type { DocumentSectionTree, SectionType } from '../../types/files'
import { verifyInsertion } from '../../utils/html-sanitization'
import { extractCrossReferenceLinksFromOoxml } from '../../utils/html-sanitization/cross-reference-links-from-ooxml'
import { injectCrossReferenceLinkFields } from '../../utils/html-sanitization/cross-reference-links-html'
import { describeTableStructure } from '../../utils/html-sanitization/describe-table-structure'
import {
  buildPlainTextWithHeadingNumbers,
  injectHeadingNumbers,
} from '../../utils/html-sanitization/inject-heading-numbers'
import { buildReviewTableHtml } from '../../utils/html-sanitization/strip-structural-table-html'
import type { WorkingDocumentSection } from '../data-connectors/imatch'
import {
  collectHeadingNumbersInRange,
  getHeadingLevel,
  insertIntoTree,
  INVALID_TEXT_SECTION_HEADING_ERROR,
  loadDocumentData,
  loadPlainTextParagraphsInRange,
  mapAnchorParagraphEntries,
  PARAGRAPH_CHUNK_SIZE,
  processHeading,
  processTable,
  resolveSectionParagraphs,
  resolveSectionRange,
  resolveTableRange,
  resolveTextSectionBodyParagraphs,
  yieldToMain,
} from './utils'

const TEXT_SECTION_ID_SUFFIX = '__text'

export type RetentionOptions = {
  /** Keep a live handle on the inserted text so telemetry can measure it again later. */
  trackForRetention?: boolean
}

export type DocumentMutationOptions = RetentionOptions & {
  trackChanges?: boolean
}

export type InsertHtmlOptions = DocumentMutationOptions & {
  location?: Word.InsertLocation
}

export interface TrackedInsertion {
  range: Word.Range
  characterCount: number
}

/**
 * Keeps hold of the text we just inserted, so we can come back later and count what is left.
 *
 * range.track() asks Word to keep the range's position up to date while the user edits
 * around or inside it. Nothing is written into the document: the handle lives in the task
 * pane's memory and is gone when the pane closes.
 *
 * It never throws. The insert has already landed in Word when this runs, so a failure here
 * must not reach the caller: the user would see "insert failed" and insert a second copy.
 */
async function trackInsertedRange(
  context: Word.RequestContext,
  range: Word.Range
): Promise<TrackedInsertion | null> {
  try {
    range.load('text')
    range.track()
    await context.sync()
    return { range, characterCount: range.text.length }
  } catch (error) {
    console.warn('[Telemetry] could not track inserted range:', error)
    return null
  }
}

// Word keeps a tracked range alive until it is released. Cleanup is best-effort: if the
// document is already gone there is nothing left to release.
async function untrackQuietly(context: Word.RequestContext, range: Word.Range): Promise<void> {
  try {
    range.untrack()
    await context.sync()
  } catch (error) {
    console.warn('[Telemetry] could not release tracked range:', error)
  }
}

/**
 * Counts how many characters of a tracked insert are still there, and how long the whole
 * document is now, then lets go of the range, also when the count fails.
 *
 * Word.run(range, …) reuses the context the range was tracked in; a fresh context
 * would not know about it.
 */
export async function measureTrackedInsertion({
  range,
}: TrackedInsertion): Promise<{ charactersRemaining: number; documentCharacters: number }> {
  return Word.run(range, async (context) => {
    try {
      range.load('text')
      const body = context.document.body
      body.load('text')
      await context.sync()

      return {
        charactersRemaining: range.text.length,
        documentCharacters: body.text.length,
      }
    } finally {
      await untrackQuietly(context, range)
    }
  })
}

export type RangeHtmlResult = {
  html: string
}

/** Read path: merge getHtml() with OOXML-scraped cross-reference links for LLM context. */
function rangeHtmlWithInjectedCrossReferences(
  html: string,
  ooxml: string | undefined
): RangeHtmlResult {
  const links = extractCrossReferenceLinksFromOoxml(ooxml ?? '')
  const injectedHtml = injectCrossReferenceLinkFields(html, links)
  return { html: injectedHtml }
}

/** Load range HTML + OOXML, apply heading numbers, then attach cross-reference link metadata. */
async function getRangeHtmlWithCrossReferenceLinks(
  context: Word.RequestContext,
  range: Word.Range
): Promise<RangeHtmlResult> {
  const { headings, queued } = await collectHeadingNumbersInRange(context, range, () => ({
    html: range.getHtml(),
    ooxml: range.getOoxml(),
  }))

  const withHeadingNumbers = injectHeadingNumbers(queued.html.value, headings)
  return rangeHtmlWithInjectedCrossReferences(withHeadingNumbers, queued.ooxml.value ?? '')
}

async function getRangePlainTextWithHeadingNumbers(
  context: Word.RequestContext,
  range: Word.Range
): Promise<string> {
  const paragraphs = await loadPlainTextParagraphsInRange(context, range)
  return buildPlainTextWithHeadingNumbers(paragraphs)
}

function isTextSectionId(sectionId: string): boolean {
  return sectionId.endsWith(TEXT_SECTION_ID_SUFFIX)
}

/**
 * Classifies a working-document section id for read paths that only have an id (e.g. agent skills).
 * Synthetic __text ids are text sections; otherwise inspects the paragraph in Word.
 */
export async function resolveSectionTypeFromId(sectionId: string): Promise<SectionType | null> {
  if (!sectionId || typeof sectionId !== 'string') {
    return null
  }

  if (isTextSectionId(sectionId)) {
    return 'text'
  }

  try {
    return await Word.run(async (context) => {
      const paragraph = context.document.getParagraphByUniqueLocalId(sectionId)
      paragraph.load('styleBuiltIn')

      const parentTable = paragraph.parentTableOrNullObject
      parentTable.load('isNullObject')

      await context.sync()

      // Same order as getDocumentHierarchicalStructure: heading before table membership.
      if (getHeadingLevel(paragraph) !== null) {
        return 'heading'
      }

      if (!parentTable.isNullObject) {
        return 'table'
      }

      return null
    })
  } catch (error) {
    console.warn(`Failed to resolve section type for id ${sectionId}:`, error)
    return null
  }
}

function getHeadingIdFromTextSectionId(sectionId: string): string {
  return sectionId.slice(0, -TEXT_SECTION_ID_SUFFIX.length)
}

/**
 * Runs a document edit while native Track Changes is active for the mutation.
 *
 * Observed on Word desktop (`document.changeTrackingMode` / `document.trackRevisions`):
 * - Track Changes off → `changeTrackingMode: "Off"`, `trackRevisions: false`
 * - Track Changes on (everyone) → `changeTrackingMode: "TrackAll"`, `trackRevisions: true`
 * - Track Changes on (mine only) → `changeTrackingMode: "TrackMineOnly"`, `trackRevisions: false`
 *
 * We rely on `changeTrackingMode` only.
 *
 * If tracking is already on (Track All / Track Mine), runs the operation as-is. If off,
 * temporarily turns on Track All for the edit, then restores the previous mode afterward.
 *
 * Must run inside an existing `Word.run` batch and requires a sync inside the operation.
 */
export async function withTrackChanges<T>(
  context: Word.RequestContext,
  operation: () => Promise<T> | T
): Promise<T> {
  const document = context.document
  document.load('changeTrackingMode')
  await context.sync()

  const previousMode = document.changeTrackingMode
  if (previousMode !== Word.ChangeTrackingMode.off) {
    return await operation()
  }

  document.changeTrackingMode = Word.ChangeTrackingMode.trackAll
  // context.sync() needed if operation doesn't provide its own before document interaction.
  // Each sync takes extra time and in this case is most likely unnecessary.
  // If any operation doesn't provide its own sync, we should add it here.

  try {
    return await operation()
  } finally {
    document.changeTrackingMode = previousMode
    await context.sync()
  }
}

/**
 * Insert text at the current selection
 */
export async function insertText(
  text: string,
  location: Word.InsertLocation = Word.InsertLocation.replace
): Promise<void> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection()
    selection.insertText(text, location)
    await context.sync()
  })
}

/**
 * Insert HTML at the specified location, matching the target document's font.
 * Reads the cursor's font name and applies it to the inserted range so content
 * doesn't fall back to Times New Roman (the HTML default).  Font size is NOT
 * overridden — Word handles heading/body proportions via the HTML tags.
 *
 * @param html - HTML content to insert (should be pre-sanitized)
 * @param options - Insert location (default replace) and optional native Track Changes
 */
export async function insertHtml(
  html: string,
  options?: InsertHtmlOptions
): Promise<TrackedInsertion | null> {
  const location = options?.location ?? Word.InsertLocation.replace
  const trackChanges = options?.trackChanges ?? false

  return Word.run(async (context) => {
    let tracked: TrackedInsertion | null = null

    const performInsert = async () => {
      const selection = context.document.getSelection()
      selection.font.load('name')
      await context.sync()

      const targetFontName = selection.font.name
      const insertedRange = selection.insertHtml(html, location)

      if (targetFontName) {
        insertedRange.font.name = targetFontName
      }

      await context.sync()

      if (options?.trackForRetention) {
        tracked = await trackInsertedRange(context, insertedRange)
      }

      if (import.meta.env.VITE_DEBUG_HTML === 'true') {
        await verifyInsertion(html, insertedRange, context)
      }
    }

    if (trackChanges) {
      await withTrackChanges(context, performInsert)
    } else {
      await performInsert()
    }
    return tracked
  })
}

/**
 * Replace the current selection with HTML by deleting it first, then inserting
 * at the former start point.
 *
 * Word (Mac) throws GeneralException on insertHtml(replace) when the selection
 * spans both body text and a table and the payload also mixes prose + table.
 * Delete-then-insert avoids that replace-on-heterogeneous-range failure.
 */
export async function replaceSelectionWithHtml(
  html: string,
  options?: DocumentMutationOptions
): Promise<TrackedInsertion | null> {
  const trackChanges = options?.trackChanges ?? false

  return Word.run(async (context) => {
    let tracked: TrackedInsertion | null = null

    const performReplace = async () => {
      const selection = context.document.getSelection()
      selection.font.load('name')
      const insertPoint = selection.getRange(Word.RangeLocation.start)
      await context.sync()

      const targetFontName = selection.font.name
      selection.delete()
      await context.sync()

      const insertedRange = insertPoint.insertHtml(html, Word.InsertLocation.replace)
      if (targetFontName) {
        insertedRange.font.name = targetFontName
      }
      await context.sync()

      if (options?.trackForRetention) {
        tracked = await trackInsertedRange(context, insertedRange)
      }
    }

    if (trackChanges) {
      await withTrackChanges(context, performReplace)
    } else {
      await performReplace()
    }
    return tracked
  })
}

/**
 * Get the current selection as HTML.
 * Returns null if nothing is selected.
 *
 * Note: getHtml() throws GeneralException during sync on a collapsed/empty selection.
 * We catch that known failure and return null — same pattern used for missing custom properties.
 */
export async function getSelectedHtml(): Promise<RangeHtmlResult | null> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection()

    try {
      return await getRangeHtmlWithCrossReferenceLinks(context, selection)
    } catch {
      return null
    }
  })
}

/**
 * Get the current selection as plain text.
 * Returns null if nothing is selected (collapsed cursor).
 */

export async function getSelectedText(): Promise<string | null> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection()
    selection.load('text')

    await context.sync()

    const text = selection.text
    // when nothing is selected, empty string is returned
    return text.length > 0 ? text : null
  })
}

export type SelectedParagraph = {
  id: string
  text: string
}

/**
 * Get selected paragraphs for QC prompts as stable paragraph IDs plus plain text.
 * Word returns the full text of each paragraph intersecting the current selection
 * (not just the highlighted substring inside a paragraph).
 * Returns an empty array when the selection does not contain paragraph content.
 *
 * Table context for cells inside the selection is provided separately as a `table_html`
 * reference entry (see buildTableReferences) rather than per-paragraph positional metadata.
 */
export async function getSelectedParagraphs(): Promise<SelectedParagraph[]> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection()
    selection.load('text')
    const paragraphs = selection.paragraphs
    paragraphs.load('uniqueLocalId, text, styleBuiltIn')
    await context.sync()

    // Collapsed cursor: Word still reports the intersecting paragraph, but nothing is selected
    if (selection.text === '') return []

    return mapAnchorParagraphEntries(context, paragraphs.items)
  })
}

/**
 * Get the full document content as plain text
 * Note: For very large documents (100+ pages), this may take several seconds to load.
 * The text includes all content but excludes formatting information.
 */
export async function getDocumentText(): Promise<string> {
  return Word.run(async (context) => {
    const body = context.document.body
    return getRangePlainTextWithHeadingNumbers(context, body.getRange())
  })
}

// Yield to the event loop to keep the UI responsive during heavy document processing
// (re-exported from utils; defined there so loadDocumentData can also use them)

/**
 * Get all headings, tables, and text sections from the document in a hierarchical tree structure
 * Returns a nested structure where entries (headings, tables, and text sections) are organized hierarchically
 * Tables are identified by their first cell content and nested under the most recent heading
 * A synthetic text entry is created under a heading when regular body text is detected in that section
 *
 * Two-sync strategy to avoid per-paragraph relationship loads:
 *  1. First sync: scalar paragraph fields (text, style, id) + table collection
 *  2. Second sync: listItemOrNullObject for heading paragraphs only + first-cell/paragraph-IDs
 *     for tables (both sets are far fewer than all body paragraphs)
 */
export async function getDocumentHierarchicalStructure(): Promise<DocumentSectionTree[]> {
  return Word.run(async (context) => {
    const { paragraphs, paragraphToTableInfo } = await loadDocumentData(context)

    const rootEntries: DocumentSectionTree[] = []
    const stack: { entry: DocumentSectionTree; level: number }[] = []
    const addedTableIds = new Set<string>()

    function ensureTextNodeForCurrentHeading() {
      if (stack.length === 0) return

      const currentHeading = stack[stack.length - 1].entry
      const textNodeId = `${currentHeading.id}${TEXT_SECTION_ID_SUFFIX}`
      const hasTextNode = currentHeading.children.some((child) => child.id === textNodeId)

      if (!hasTextNode) {
        // Text node is always first under a heading.
        currentHeading.children.unshift({
          id: textNodeId,
          type: 'text',
          title: currentHeading.title,
          children: [],
        })
      }
    }

    for (let i = 0; i < paragraphs.items.length; i++) {
      if (i % PARAGRAPH_CHUNK_SIZE === 0) await yieldToMain()

      const paragraph = paragraphs.items[i]
      const paragraphText = paragraph.text.trim()
      if (!paragraphText) continue

      const headingLevel = getHeadingLevel(paragraph)

      if (headingLevel !== null) {
        const listItem = paragraph.listItemOrNullObject
        const sectionNumber = listItem.isNullObject ? '' : (listItem.listString ?? '')
        const heading: DocumentSectionTree = {
          id: paragraph.uniqueLocalId,
          type: 'heading',
          title: sectionNumber ? `${sectionNumber} ${paragraphText}` : paragraphText,
          children: [],
        }
        processHeading({ heading, headingLevel, stack, rootEntries })
      } else {
        const tableInfo = paragraphToTableInfo.get(paragraph.uniqueLocalId)
        if (tableInfo) {
          processTable({
            tableTitle: tableInfo.title,
            paragraphId: tableInfo.id,
            stack,
            rootEntries,
            addedTableIds,
          })
        } else {
          ensureTextNodeForCurrentHeading()
        }
      }
    }

    return rootEntries
  })
}

/**
 * Get the HTML content of a specific section identified by its paragraph ID
 * A section includes all content from the heading until the next heading of same or higher level
 *
 * @param paragraphId - The unique paragraph ID (uniqueLocalId) of the heading
 * @returns HTML content of the section, or null if the paragraph no longer exists in the document
 * @throws Error if paragraphId is invalid
 */
export async function getSectionContentById(paragraphId: string): Promise<RangeHtmlResult | null> {
  if (!paragraphId || typeof paragraphId !== 'string') {
    throw new Error('Invalid paragraphId: must be a non-empty string')
  }

  try {
    return await Word.run(async (context) => {
      const sectionRange = await resolveSectionRange(context, paragraphId)
      return await getRangeHtmlWithCrossReferenceLinks(context, sectionRange)
    })
  } catch (error) {
    console.warn(`Failed to fetch section content for paragraphId ${paragraphId}:`, error)
    return null
  }
}

/**
 * Get text-only HTML content for a synthetic text section id (<headingId>__text).
 *
 * Includes only direct body paragraphs under the heading (before the next heading),
 * excluding table paragraphs and any heading paragraphs.
 */
export async function getSectionTextById(sectionId: string): Promise<RangeHtmlResult | null> {
  if (!sectionId || typeof sectionId !== 'string') {
    throw new Error('Invalid sectionId: must be a non-empty string')
  }

  if (!isTextSectionId(sectionId)) {
    throw new Error('Invalid sectionId: expected synthetic text section id')
  }

  const headingId = getHeadingIdFromTextSectionId(sectionId)

  try {
    return await Word.run(async (context) => {
      const paragraphs = await resolveTextSectionBodyParagraphs(context, headingId, (paragraph) => {
        const range = paragraph.getRange()
        return { html: range.getHtml(), ooxml: range.getOoxml() }
      })

      if (paragraphs.length === 0) {
        return { html: '' }
      }

      const html = paragraphs
        .filter(({ data }) => typeof data.html.value === 'string' && data.html.value.length > 0)
        .map(
          ({ data }) =>
            rangeHtmlWithInjectedCrossReferences(data.html.value, data.ooxml.value ?? '').html
        )
        .join('')

      return { html }
    })
  } catch (error) {
    if (error instanceof Error && error.name === INVALID_TEXT_SECTION_HEADING_ERROR) {
      console.error(`Invalid text section heading for sectionId ${sectionId}:`, error)
      throw error
    }

    console.warn(`Failed to fetch section text content for sectionId ${sectionId}:`, error)
    return null
  }
}

/**
 * Get body-only plain text for a synthetic text section id (<headingId>__text).
 * Mirrors getSectionTextById but returns newline-joined plain text instead of HTML.
 * Uses resolveTextSectionBodyParagraphs — table paragraphs are excluded.
 *
 * @param sectionId - A synthetic __text section ID
 * @returns Newline-joined plain text of body paragraphs, '' if empty, null on error
 */
export async function getSectionTextAsPlainTextById(sectionId: string): Promise<string | null> {
  if (!sectionId || typeof sectionId !== 'string') {
    throw new Error('Invalid sectionId: must be a non-empty string')
  }

  if (!isTextSectionId(sectionId)) {
    throw new Error('Invalid sectionId: expected synthetic text section id')
  }

  const headingId = getHeadingIdFromTextSectionId(sectionId)

  try {
    return await Word.run(async (context) => {
      // text is already loaded in sync 1 — pass () => null, nothing extra to queue
      const paragraphs = await resolveTextSectionBodyParagraphs(context, headingId, () => null)

      if (paragraphs.length === 0) return ''

      return paragraphs.map(({ text }) => text).join('\n')
    })
  } catch (error) {
    if (error instanceof Error && error.name === INVALID_TEXT_SECTION_HEADING_ERROR) {
      console.error(`Invalid text section heading for sectionId ${sectionId}:`, error)
      throw error
    }

    console.warn(`Failed to fetch section text plain content for sectionId ${sectionId}:`, error)
    return null
  }
}

/**
 * Body paragraphs under a synthetic text section id ({@link headingId}__text), each with a
 * Word uniqueLocalId suitable for comment anchoring.
 */
export async function getTextSectionParagraphsById(
  sectionId: string
): Promise<{ id: string; text: string }[]> {
  if (!sectionId || typeof sectionId !== 'string') {
    throw new Error('Invalid sectionId: must be a non-empty string')
  }

  if (!isTextSectionId(sectionId)) {
    throw new Error('Invalid sectionId: expected synthetic text section id')
  }

  const headingId = getHeadingIdFromTextSectionId(sectionId)

  try {
    return await Word.run(async (context) => {
      const paragraphs = await resolveTextSectionBodyParagraphs(context, headingId, (paragraph) => {
        paragraph.load('uniqueLocalId')
        return paragraph
      })

      return paragraphs.map(({ text, data: paragraph }) => ({
        id: paragraph.uniqueLocalId,
        text,
      }))
    })
  } catch (error) {
    if (error instanceof Error && error.name === INVALID_TEXT_SECTION_HEADING_ERROR) {
      console.error(`Invalid text section heading for sectionId ${sectionId}:`, error)
      throw error
    }

    console.warn(`Failed to fetch text section paragraphs for sectionId ${sectionId}:`, error)
    return []
  }
}

/**
 * Every anchorable paragraph in a heading's full section: the heading itself plus every paragraph
 * down to the next heading of the same or higher level — including sub-headings, their body text,
 * and table paragraphs. Each carries a Word uniqueLocalId suitable for comment anchoring.
 *
 * Unlike getTextSectionParagraphsById (direct body of one heading only), this covers the whole
 * subtree, so comment-mode reviews of a heading selection can anchor per paragraph instead of
 * collapsing every comment onto the heading. A synthetic __text id is delegated to
 * getTextSectionParagraphsById (direct body only), so this is a safe entry point for both kinds.
 *
 * @param sectionId - The uniqueLocalId of a heading paragraph, or a __text section id
 * @returns Non-empty section paragraphs with ids, or [] on error
 */
export async function getSectionParagraphsById(
  sectionId: string
): Promise<{ id: string; text: string }[]> {
  if (!sectionId || typeof sectionId !== 'string') {
    throw new Error('Invalid sectionId: must be a non-empty string')
  }

  // A __text id means "direct body only" — that's a different walker. Delegate so this
  // function is a safe single entry point for both a heading id and a synthetic __text id.
  if (isTextSectionId(sectionId)) {
    return getTextSectionParagraphsById(sectionId)
  }

  try {
    return await Word.run((context) => resolveSectionParagraphs(context, sectionId))
  } catch (error) {
    if (error instanceof Error && error.name === INVALID_TEXT_SECTION_HEADING_ERROR) {
      console.error(`Invalid section heading for sectionId ${sectionId}:`, error)
      throw error
    }

    console.warn(`Failed to fetch section paragraphs for sectionId ${sectionId}:`, error)
    return []
  }
}

/**
 * Every non-empty paragraph in the document body, each with a Word uniqueLocalId suitable
 * for comment anchoring.
 */
export async function getDocumentParagraphs(): Promise<{ id: string; text: string }[]> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs
    paragraphs.load('uniqueLocalId, text, styleBuiltIn')
    await context.sync()

    return mapAnchorParagraphEntries(context, paragraphs.items)
  })
}

/**
 * Get section content as plain text by paragraph ID.
 * Mirrors getSectionContentById but returns the range's .text instead of HTML.
 * Accepts both heading paragraph IDs and synthetic text section IDs (<headingId>__text);
 * the __text suffix is stripped so the heading paragraph is resolved correctly.
 *
 * @param paragraphId - The uniqueLocalId of the heading paragraph, or a __text section ID
 * @returns Plain text of the section, or null if the paragraph no longer exists
 */
export async function getSectionPlainTextById(paragraphId: string): Promise<string | null> {
  if (!paragraphId || typeof paragraphId !== 'string') {
    throw new Error('Invalid paragraphId: must be a non-empty string')
  }

  const resolvedId = isTextSectionId(paragraphId)
    ? getHeadingIdFromTextSectionId(paragraphId)
    : paragraphId

  try {
    return await Word.run(async (context) => {
      const sectionRange = await resolveSectionRange(context, resolvedId)
      const text = await getRangePlainTextWithHeadingNumbers(context, sectionRange)
      return text.trim() || null
    })
  } catch (error) {
    console.warn(`Failed to fetch section plain text for paragraphId ${paragraphId}:`, error)
    return null
  }
}

/**
 * Get table content by a paragraph ID that belongs to the table
 * Returns the table data as HTML
 *
 * @param tableParagraphId - The unique paragraph ID (uniqueLocalId) of any paragraph in the table
 * @returns HTML string representation of the table, or null if the paragraph no longer exists in the document
 */
export async function getTableByParagraphId(
  tableParagraphId: string
): Promise<RangeHtmlResult | null> {
  try {
    return await Word.run(async (context) => {
      const tableRange = resolveTableRange(context, tableParagraphId)
      const html = tableRange.getHtml()
      const ooxml = tableRange.getOoxml()
      await context.sync()
      return rangeHtmlWithInjectedCrossReferences(html.value, ooxml.value)
    })
  } catch (error) {
    console.warn(`Failed to fetch table content for paragraphId ${tableParagraphId}:`, error)
    return null
  }
}

/**
 * Get table content as plain text by a paragraph ID that belongs to the table.
 * Mirrors getTableByParagraphId but returns the range's .text instead of HTML.
 *
 * @param tableParagraphId - The uniqueLocalId of any paragraph inside the table
 * @returns Plain text of the table, or null if the paragraph no longer exists
 */
export async function getTablePlainTextByParagraphId(
  tableParagraphId: string
): Promise<string | null> {
  try {
    return await Word.run(async (context) => {
      const tableRange = resolveTableRange(context, tableParagraphId)
      tableRange.load('text')
      await context.sync()
      return tableRange.text.trim() || null
    })
  } catch (error) {
    console.warn(`Failed to fetch table plain text for paragraphId ${tableParagraphId}:`, error)
    return null
  }
}

/**
 * Replace the fully-selected table with new HTML.
 * Only triggers when the user has selected the entire table (e.g. by clicking
 * the table move-handle top-left of the table). A cursor placed inside a cell
 * does NOT qualify — use getSelection().tables to distinguish the two cases:
 * tables fully enclosed by the selection vs. the parent table of the cursor.
 *
 * @returns `{ replaced }` — true if one whole table was selected and replaced — and the
 * tracked range when retention tracking was requested.
 */
export async function replaceSelectedTable(
  html: string,
  options?: DocumentMutationOptions
): Promise<{ replaced: boolean; tracked: TrackedInsertion | null }> {
  const trackChanges = options?.trackChanges ?? false

  return Word.run(async (context) => {
    let replaced = false
    let tracked: TrackedInsertion | null = null

    const performReplace = async () => {
      const selection = context.document.getSelection()
      const tables = selection.tables
      tables.load('items')
      await context.sync()

      if (tables.items.length === 0) {
        return
      }

      // Replace the first (and normally only) fully-selected table
      const tableRange = tables.items[0].getRange()
      const insertedRange = tableRange.insertHtml(html, Word.InsertLocation.replace)
      await context.sync()

      replaced = true
      tracked = options?.trackForRetention ? await trackInsertedRange(context, insertedRange) : null

      if (import.meta.env.VITE_DEBUG_HTML === 'true') {
        await verifyInsertion(html, insertedRange, context)
      }
    }

    if (trackChanges) {
      await withTrackChanges(context, performReplace)
    } else {
      await performReplace()
    }
    return { replaced, tracked }
  })
}

/**
 * Generic Word.run wrapper for custom operations
 * Use this for operations not covered by the above functions
 */
export async function runWordOperation<T>(
  operation: (context: Word.RequestContext) => Promise<T>
): Promise<T> {
  return Word.run(operation)
}

/**
 * Build a hierarchical snapshot of the working document for the /imatch API.
 *
 * Uses loadDocumentData (shared with getDocumentHierarchicalStructure) for the
 * two-sync load, then walks paragraphs once to build a nested tree where each
 * section owns only the body text directly under it — zero duplication, full
 * coverage. IDs are uniqueLocalIds, matching getSectionContentById /
 * getTableByParagraphId so API response section_ids round-trip cleanly.
 */
export async function getWorkingDocumentSnapshot(): Promise<WorkingDocumentSection[]> {
  return Word.run(async (context) => {
    const { paragraphs, paragraphToTableInfo } = await loadDocumentData(context)

    const snapshot: WorkingDocumentSection[] = []
    const stack: { entry: WorkingDocumentSection; level: number }[] = []
    let currentHeading: WorkingDocumentSection | null = null
    const tableSectionMap = new Map<string, WorkingDocumentSection>()

    for (let i = 0; i < paragraphs.items.length; i++) {
      if (i % PARAGRAPH_CHUNK_SIZE === 0) await yieldToMain()

      const paragraph = paragraphs.items[i]
      const text = paragraph.text.trim()
      if (!text) continue

      const headingLevel = getHeadingLevel(paragraph)

      if (headingLevel !== null) {
        const listItem = paragraph.listItemOrNullObject
        const sectionNumber = listItem.isNullObject ? '' : (listItem.listString ?? '')
        currentHeading = {
          id: paragraph.uniqueLocalId,
          type: 'heading',
          title: sectionNumber ? `${sectionNumber} ${text}` : text,
          level: headingLevel,
          content: '',
          children: [],
        }
        insertIntoTree(currentHeading, headingLevel, stack, snapshot)
        continue
      }

      const tableInfo = paragraphToTableInfo.get(paragraph.uniqueLocalId)
      if (tableInfo) {
        if (!tableSectionMap.has(tableInfo.id)) {
          const tableSection: WorkingDocumentSection = {
            id: tableInfo.id,
            type: 'table',
            title: tableInfo.title,
            content: '',
            children: [],
          }
          const parentLevel = stack.length > 0 ? stack[stack.length - 1].level : 0
          insertIntoTree(tableSection, parentLevel + 1, stack, snapshot)
          tableSectionMap.set(tableInfo.id, tableSection)
        }
        const tableSection = tableSectionMap.get(tableInfo.id)!
        tableSection.content = tableSection.content ? `${tableSection.content}\n${text}` : text
        continue
      }

      // Plain body paragraph — content belongs to the nearest parent heading
      if (currentHeading !== null) {
        currentHeading.content = currentHeading.content
          ? `${currentHeading.content}\n${text}`
          : text
      }
    }

    return snapshot
  })
}

const QC_COMMENT_PREFIX = '**AI-generated comment**\n\n'

/**
 * Result of a Word comment batch (new inserts or replies to existing threads).
 * `commentIds` is index-aligned with the input array; null when that item was not applied.
 */
export type WordCommentBatchResult = {
  insertedCount: number
  commentIds: (string | null)[]
}

/**
 * Appends AI replies to existing Word comment threads (Comment.reply), as opposed
 * to insertReviewComments which inserts brand-new anchored comments. A reply only needs
 * the thread's `comment.id` - no paragraph lookup or search_text anchoring.
 *
 * The LLM echoes back `comment_id` from what we sent it, so it can be stale or
 * hallucinated (e.g. thread deleted between read and write). We skip unmatched
 * ids rather than failing the whole batch - same partial-success policy as
 * insertReviewComments skipping unresolved paragraphs.
 */
export async function addCommentReplies(replies: CommentReply[]): Promise<WordCommentBatchResult> {
  if (replies.length === 0) {
    return { insertedCount: 0, commentIds: [] }
  }

  return Word.run(async (context) => {
    const comments: Word.CommentCollection = context.document.body.getRange().getComments()
    comments.load('items/id')
    await context.sync()

    const commentsById = new Map(comments.items.map((comment) => [comment.id, comment]))

    const commentIds: (string | null)[] = replies.map(() => null)
    let insertedCount = 0
    for (const [index, reply] of replies.entries()) {
      const comment = commentsById.get(reply.commentId)
      if (!comment) {
        console.warn(
          `[addCommentReplies] no comment thread found for commentId "${reply.commentId}" - skipping reply`
        )
        continue
      }

      comment.reply(`${QC_COMMENT_PREFIX}${reply.commentText}`)
      commentIds[index] = reply.commentId
      insertedCount++
    }

    await context.sync()

    if (insertedCount < replies.length) {
      console.warn(
        `[addCommentReplies] replied to ${insertedCount}/${replies.length} comment threads`
      )
    }

    return { insertedCount, commentIds }
  })
}

export type InsertReviewCommentsOptions = {
  /** Mouse-selection review: anchor lookup to these paragraph IDs. */
  selectionParagraphIds?: string[]
  /** Section/full-document review: resolve each comment paragraph by uniqueLocalId. */
  resolveDocumentWide?: boolean
}

type ReviewCommentSearchEntry = {
  comment: ReviewComment
  searchResults: Word.RangeCollection
}

type ReviewCommentPendingInsert = {
  index: number
  insertedComment: Word.Comment
}

/**
 * Adds the AI's review comments into the Word document.
 *
 * Each comment knows which paragraph it belongs to (comment.paragraphId) and,
 * optionally, the exact words inside that paragraph it should attach to
 * (comment.searchText). This function's job is:
 *   1. Find the real Word paragraph for every comment.
 *   2. Pin the comment to the specific words if we know them, otherwise to the
 *      whole paragraph.
 *   3. Actually insert the comments.
 *
 * There are two ways we look up paragraphs:
 *   - resolveDocumentWide = true  -> search the ENTIRE document by ID.
 *     (used when reviewing a whole section or the full document)
 *   - resolveDocumentWide = false -> only look inside the text the user had
 *     highlighted with the mouse.
 *
 * A note on `context.sync()`: talking to Word is slow, so we batch up work and
 * then call sync() once to send it all at once. Fewer syncs = faster.
 */
export async function insertReviewComments(
  comments: ReviewComment[],
  options: InsertReviewCommentsOptions = {}
): Promise<WordCommentBatchResult> {
  if (comments.length === 0) {
    return { insertedCount: 0, commentIds: [] }
  }

  const { selectionParagraphIds = [], resolveDocumentWide = false } = options

  return Word.run(async (context) => {
    // Lookup table: paragraph ID -> the actual Word paragraph object.
    const paragraphMap = new Map<string, Word.Paragraph>()

    if (resolveDocumentWide) {
      // Grab the unique paragraph IDs (several comments may share one paragraph).
      const uniqueIds = [...new Set(comments.map((comment) => comment.paragraphId))]

      // Ask Word for every paragraph at once, then fetch them all in a single sync.
      // This is the fast path.
      for (const paragraphId of uniqueIds) {
        paragraphMap.set(paragraphId, context.document.getParagraphByUniqueLocalId(paragraphId))
      }

      try {
        await context.sync()
      } catch {
        // If even ONE paragraph ID is stale (e.g. the user deleted it), Word rejects
        // the whole batch. So we start over and fetch them one-by-one instead,
        // keeping the good ones and quietly skipping the deleted ones.
        paragraphMap.clear()
        for (const paragraphId of uniqueIds) {
          try {
            const paragraph = context.document.getParagraphByUniqueLocalId(paragraphId)
            await context.sync()
            paragraphMap.set(paragraphId, paragraph)
          } catch {
            // This paragraph no longer exists - skip it, don't lose the others.
            console.warn(
              `[insertReviewComments] paragraphId "${paragraphId}" not found in document (likely deleted/edited since it was sent) - skipping`
            )
          }
        }
      }
    } else {
      // Mouse-selection mode. We saved the first and last paragraph IDs of what
      // the user highlighted BEFORE sending it to the AI. We rebuild that same
      // highlighted range now, so the comments land where the user selected -
      // even if they clicked somewhere else while waiting for the AI.
      let paragraphs: Word.ParagraphCollection

      if (selectionParagraphIds.length > 0) {
        const firstId = selectionParagraphIds[0]
        const lastId = selectionParagraphIds[selectionParagraphIds.length - 1]
        const firstParagraph = context.document.getParagraphByUniqueLocalId(firstId)
        const lastParagraph = context.document.getParagraphByUniqueLocalId(lastId)
        // expandTo() stretches the range from the first paragraph to the last,
        // covering everything in between.
        paragraphs = firstParagraph.getRange().expandTo(lastParagraph.getRange()).paragraphs
      } else {
        // We never captured a selection - just use wherever the cursor is now.
        paragraphs = context.document.getSelection().paragraphs
      }

      paragraphs.load('items/uniqueLocalId')

      try {
        await context.sync()
      } catch {
        // The saved paragraphs were deleted while the AI was thinking.
        // Fall back to the user's current cursor position.
        paragraphs = context.document.getSelection().paragraphs
        paragraphs.load('items/uniqueLocalId')
        await context.sync()
      }

      for (const paragraph of paragraphs.items) {
        paragraphMap.set(paragraph.uniqueLocalId, paragraph)
      }
    }

    // Some comments point at specific words inside a paragraph (searchText).
    // Here we ask Word to find those words. We queue up ALL the searches first,
    // then run them together in one sync. Comments without searchText are skipped
    // here - they'll just attach to the whole paragraph further down.
    const searchEntries: ReviewCommentSearchEntry[] = []
    for (const comment of comments) {
      const paragraph = paragraphMap.get(comment.paragraphId)
      if (!paragraph || !comment.searchText) continue
      const results = paragraph.getRange().search(comment.searchText, { matchCase: false })
      results.load('items')
      searchEntries.push({ comment, searchResults: results })
    }

    if (searchEntries.length > 0) {
      await context.sync() // run all the word searches at once
    }

    const searchMap = new Map(
      searchEntries.map(({ comment, searchResults }) => [comment, searchResults])
    )

    // Now actually add each comment. We queue them all, then send in one sync.
    const commentIds: (string | null)[] = comments.map(() => null)
    const pendingInserts: ReviewCommentPendingInsert[] = []
    let insertedCount = 0

    for (const [index, comment] of comments.entries()) {
      const paragraph = paragraphMap.get(comment.paragraphId)
      if (!paragraph) {
        // Paragraph id is real but wasn't in the searched set (resolveDocumentWide fallback
        // dropped it above, or - in selection mode - the saved/live selection didn't cover it).
        console.warn(
          `[insertReviewComments] no paragraph resolved for paragraphId "${comment.paragraphId}" ` +
            `(resolveDocumentWide=${resolveDocumentWide}) - skipping comment: "${comment.commentText.slice(0, 80)}"`
        )
        continue
      }

      const fullCommentText = `${QC_COMMENT_PREFIX}${comment.commentText}`
      const searchResults = searchMap.get(comment)
      // If we found the specific words, attach the comment there.
      // Otherwise attach it to the whole paragraph.
      const target =
        searchResults && searchResults.items.length > 0
          ? searchResults.items[0]
          : paragraph.getRange()
      const insertedComment = target.insertComment(fullCommentText)
      insertedComment.load('id')
      pendingInserts.push({ index, insertedComment })
      insertedCount++
    }

    await context.sync() // send all the comments to Word at once

    for (const { index, insertedComment } of pendingInserts) {
      commentIds[index] = insertedComment.id
    }

    if (insertedCount < comments.length) {
      console.warn(`[insertReviewComments] inserted ${insertedCount}/${comments.length} comments`)
    }

    return { insertedCount, commentIds }
  })
}

/**
 * Scrolls the document view to an existing comment thread and selects its anchor range.
 * Uses comment.id from insertComment / getFullDocumentComments — there is no goToById for comments.
 */
export async function navigateToWordComment(commentId: string): Promise<void> {
  return Word.run(async (context) => {
    const comments: Word.CommentCollection = context.document.body.getComments()
    comments.load('items/id')
    await context.sync()

    const comment = comments.items.find((item) => item.id === commentId)
    if (!comment) {
      throw new Error(`[navigateToWordComment] comment "${commentId}" not found`)
    }

    comment.getRange().select()
    await context.sync()
  })
}

/**
 * Read every comment in the working document along with the info the backend/LLM
 * needs to re-locate it: the comment text, the paragraph it is anchored to
 * (`paragraph_id`), and the anchored text itself (`search_text`). This lets the
 * model reason about existing reviewer comments and lets us re-attach responses.
 *
 * Talking to Word is slow, so we do it in two batched round-trips rather than one
 * per comment: first fetch the comment collection, then queue the anchor lookups
 * for every comment and resolve them all in a single sync.
 */
export async function getFullDocumentComments(): Promise<CurrentDocumentCommentEntry[]> {
  return Word.run(async (context) => {
    // body.getComments() is WordApi 1.4 (Online + Desktop). Prefer this over
    // document.comments, which is WordApiDesktop-only.
    const comments: Word.CommentCollection = context.document.body.getComments()
    comments.load('items')
    await context.sync()

    if (comments.items.length === 0) return []

    // Queue the anchor lookups for all comments, then resolve them in one sync.
    const anchors = comments.items.map((comment) => {
      comment.load('id, contentRange/text, authorEmail, authorName, creationDate, resolved')
      const range: Word.Range = comment.getRange()
      range.load('text') // anchored text in the doc -> search_text
      range.paragraphs.load('items/uniqueLocalId') // anchor paragraph ID
      return { comment, range }
    })
    await context.sync()

    return anchors.map(({ comment, range }) => {
      const entry: CurrentDocumentCommentEntry = {
        comment_id: comment.id,
        comment_text: comment.contentRange.text,
        // A comment can be anchored to text spanning zero paragraphs (e.g. the anchor
        // was deleted); fall back to no paragraph so one odd comment can't break the send.
        paragraph_id: range.paragraphs.items[0]?.uniqueLocalId,
        search_text: range.text,
      }

      if (comment.authorEmail) {
        entry.author_email = comment.authorEmail
      }
      if (comment.authorName) {
        entry.author_name = comment.authorName
      }
      if (comment.creationDate) {
        entry.creation_date = comment.creationDate.toISOString()
      }
      if (comment.resolved !== undefined) {
        entry.resolved = comment.resolved
      }

      return entry
    })
  })
}

/**
 * Cheap presence check for "does this document have any comments at all" -
 * used to enable/disable the comments context badge, not to read comment content.
 *
 * Deliberately skips the anchor-resolution round-trip getFullDocumentComments() needs
 * (contentRange/getRange/paragraphs loads): this only needs a count, so it does a
 * single sync instead of two.
 */
export async function hasDocumentComments(): Promise<boolean> {
  return Word.run(async (context) => {
    // body.getComments() is WordApi 1.4 (Online + Desktop). Prefer this over
    // document.comments, which is WordApiDesktop-only.
    const comments: Word.CommentCollection = context.document.body.getComments()
    comments.load('items')
    await context.sync()

    return comments.items.length > 0
  })
}

/**
 * Returns whether the current Word selection contains any comments.
 * Used to warn the user before inserting content that would remove them.
 *
 * getSelection().getComments() scopes the check to the selected range only,
 * unlike hasDocumentComments() which checks the whole body.
 */
export async function hasSelectionComments(): Promise<boolean> {
  return Word.run(async (context) => {
    const comments: Word.CommentCollection = context.document.getSelection().getComments()
    comments.load('items')
    await context.sync()

    return comments.items.length > 0
  })
}

/**
 * Flatten a table into its non-empty cell paragraphs, given any paragraph ID
 * inside the table. Returns each cell paragraph's uniqueLocalId + text, so
 * comment-mode reviews can anchor per cell. Table structure is not preserved.
 */
export async function getTableIds(
  tableParagraphId: string
): Promise<{ id: string; text: string }[]> {
  try {
    return await Word.run(async (context) => {
      const tableRange = resolveTableRange(context, tableParagraphId)
      const paragraphs = tableRange.paragraphs
      paragraphs.load('uniqueLocalId, text')
      await context.sync()
      return paragraphs.items
        .map((p) => ({ id: p.uniqueLocalId, text: p.text.trim() }))
        .filter((p) => p.text.length > 0)
    })
  } catch (error) {
    console.warn(`Failed to fetch table cell ids for paragraphId ${tableParagraphId}:`, error)
    return []
  }
}

/**
 * For every distinct table touched by any of the given target paragraph ids, returns one
 * `table_html` reference entry containing a structure summary prepended to the normalized
 * table HTML (non-structural markup stripped, only in-scope `<p id>` anchors kept).
 * Callers append these to `current_document_context` so a review comment anchored to a
 * single cell still comes with the row/column context an LLM needs to reason about the table.
 *
 * Targets outside any table are ignored. Tables are deduped so several targets inside
 * the same table only produce one entry.
 *
 * 1 sync: Discovery scans document.body.tables (no html).
 * 2 sync: We load paragraph ids for each table (no html, only ids) and find matches with target set.
 * 3 sync: For each match we load the table's HTML and build a `table_html` reference entry. (now html!)
 *
 * Returns:
 *   - references: one `table_html` entry per distinct table touched by any target
 *   - targetIdsInTables: the subset of targetParagraphIds that were found in tables
 */
export type TableReferenceResult = {
  references: Extract<CurrentDocumentContextEntry, { type: 'table_html' }>[]
  /** Target paragraph ids whose text is already represented in a table_html reference. */
  targetIdsInTables: ReadonlySet<string>
}

type TableWithTargets = {
  representativeTargetId: string
  targetIds: string[]
}

async function findTablesContainingTargets(targetIdSet: ReadonlySet<string>): Promise<{
  matches: TableWithTargets[]
  htmlByRepresentative: Map<string, string | null>
}> {
  return Word.run(async (context) => {
    // Load all tables in the document
    const tables = context.document.body.tables
    tables.load('items')
    await context.sync()
    if (tables.items.length === 0) {
      return { matches: [], htmlByRepresentative: new Map() }
    }

    // For each table load its ids
    const tableEntries = tables.items.map((table) => {
      const tableRange = table.getRange('Whole')
      return {
        paragraphs: tableRange.paragraphs,
      }
    })
    tableEntries.forEach(({ paragraphs }) => paragraphs.load('uniqueLocalId'))
    await context.sync()

    const matches: TableWithTargets[] = []
    const matchingRanges: Word.Range[] = [] // Word.Range handles for the HTML sync (sync 3)

    const htmlByRepresentative = new Map<string, string | null>()
    for (let i = 0; i < tableEntries.length; i++) {
      const { paragraphs } = tableEntries[i]
      const targetIds = paragraphs.items
        .map((paragraph) => paragraph.uniqueLocalId)
        .filter((id) => targetIdSet.has(id))
      if (targetIds.length === 0) continue

      matches.push({ representativeTargetId: targetIds[0], targetIds })
      matchingRanges.push(tables.items[i].getRange('Whole'))
    }

    if (matches.length === 0) {
      return { matches: [], htmlByRepresentative: new Map() }
    }

    const htmlProxies = matchingRanges.map((range) => range.getHtml())
    const ooxmlProxies = matchingRanges.map((range) => range.getOoxml())
    await context.sync()
    matches.forEach(({ representativeTargetId }, i) => {
      const injected = rangeHtmlWithInjectedCrossReferences(
        htmlProxies[i].value,
        ooxmlProxies[i].value
      )
      htmlByRepresentative.set(representativeTargetId, injected.html)
    })

    return { matches, htmlByRepresentative }
  })
}

function buildTableReferenceEntries(
  tableMatches: TableWithTargets[],
  htmlByRepresentativeTargetId: Map<string, string | null>
): TableReferenceResult {
  const references: Extract<CurrentDocumentContextEntry, { type: 'table_html' }>[] = []
  const targetIdsInTables = new Set<string>()

  for (const { representativeTargetId, targetIds } of tableMatches) {
    const html = htmlByRepresentativeTargetId.get(representativeTargetId)
    if (!html) continue

    const reviewHtml = buildReviewTableHtml(html, new Set(targetIds))
    references.push({
      type: 'table_html',
      content: `${describeTableStructure(html)}\n${reviewHtml}`,
    })
    for (const targetId of targetIds) {
      targetIdsInTables.add(targetId)
    }
  }

  return { references, targetIdsInTables }
}

export async function buildTableReferences(
  targetParagraphIds: string[]
): Promise<TableReferenceResult> {
  if (targetParagraphIds.length === 0) {
    return { references: [], targetIdsInTables: new Set() }
  }

  try {
    const { matches, htmlByRepresentative } = await findTablesContainingTargets(
      new Set(targetParagraphIds)
    )

    if (matches.length === 0) {
      return { references: [], targetIdsInTables: new Set() }
    }

    return buildTableReferenceEntries(matches, htmlByRepresentative)
  } catch (error) {
    console.warn('Failed to build table references; sending without table_html context', error)
    return { references: [], targetIdsInTables: new Set() }
  }
}
